import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { z } from 'zod';
import { useScopedNumericQuery } from './useScopedNumericQuery';
import { useSessionScopeFence } from './useSessionScope';
import { isSocketReady, releaseSocket, retainSocket } from '@/services/wsClient';
import {
  verifiedChangeClient,
  type VerifiedChangeFilter,
  type VerifiedChangePage,
} from '@/services/verified-change-client';
import type { RequestOptions } from '@/services/apiClient';
import type { RealtimeEventItem } from '@/services/realtimeClient';
import {
  applyVerifiedChangeSeenReceipt,
  emptyVerifiedChangeState,
  readVerifiedChangeEvent,
  readyVerifiedChanges,
  receiveVerifiedChanges,
  VERIFIED_CHANGE_ATTENTION_MS,
  VERIFIED_CHANGE_CATALOG,
  verifiedChangePresentation,
  verifiedChangeScopeKey,
  type VerifiedChangeScope,
  type VerifiedChangeState,
  type VerifiedChange,
} from '@/components/dashboard/verified-change';

const readySchema = z.object({
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  timestamp: z.string().datetime(),
});

/** New events request finite attention; hydration and replay only restore quiet unseen evidence. */
export function useVerifiedChanges(
  options: {
    enabled?: boolean;
    limit?: number;
    onNewChange?: (change: VerifiedChange) => void | Promise<unknown>;
  } & VerifiedChangeFilter = {},
) {
  const {
    identity,
    enabled: sessionEnabled,
    isCurrent,
  } = useSessionScopeFence(PERMISSIONS.DASHBOARD_READ);
  const filterKey = JSON.stringify([
    options.outcomes ? [...new Set(options.outcomes)].sort() : null,
    options.entityIds ? [...new Set(options.entityIds)].sort() : null,
  ]);
  const filter = useMemo<VerifiedChangeFilter>(() => {
    const [outcomes, entityIds] = JSON.parse(filterKey) as [
      VerifiedChangeFilter['outcomes'] | null,
      VerifiedChangeFilter['entityIds'] | null,
    ];
    return { ...(outcomes ? { outcomes } : {}), ...(entityIds ? { entityIds } : {}) };
  }, [filterKey]);
  const enabled =
    sessionEnabled &&
    options.enabled !== false &&
    filter.outcomes?.length !== 0 &&
    filter.entityIds?.length !== 0;
  const limit = options.limit ?? 30;
  const scope = useMemo<VerifiedChangeScope | null>(() => {
    if (!identity.tenantId || !identity.userId || !identity.sessionOrigin) return null;
    const parsed = z.array(z.string()).safeParse(JSON.parse(identity.permissions));
    return parsed.success
      ? {
          tenantId: identity.tenantId,
          userId: identity.userId,
          permissions: parsed.data,
          sessionOrigin: identity.sessionOrigin,
          observerKey: filterKey,
        }
      : null;
  }, [identity, filterKey]);
  const scopeKey = scope ? verifiedChangeScopeKey(scope) : '';
  const reducedMotion = useReducedMotion() === true;
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;
  const [state, setState] = useState<VerifiedChangeState | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const onNewChange = useRef(options.onNewChange);
  onNewChange.current = options.onNewChange;
  const reconciled = useRef(new Set<string>());
  const reconciledThrough = useRef<number>(0);
  const ready = useRef<{ scopeKey: string; timestamp: number } | null>(null);
  const [receiptError, setReceiptError] = useState<{ scopeKey: string; error: Error } | null>(null);
  const [clockRevision, refreshClock] = useState(0);
  const writes = useRef(new Set<AbortController>());
  const [olderLoading, setOlderLoading] = useState(false);
  const continuation = useRef<string | null>(null);
  const readWindow = useCallback(
    async (request: RequestOptions, cursor?: string): Promise<VerifiedChangePage> => {
      request.signal?.throwIfAborted();
      const result = await verifiedChangeClient.list(limit, request, cursor, filter);
      if (cursor && result.cursor === cursor)
        throw new Error('Observed-change continuation repeated a cursor');
      return result;
    },
    [limit, filter],
  );
  const page = useScopedNumericQuery(
    ['dashboard', 'verified-changes', limit, filterKey],
    PERMISSIONS.DASHBOARD_READ,
    (request) => readWindow(request),
    { enabled, refetchInterval: 30_000 },
  );
  const latestPage = useRef(page.data);
  latestPage.current = page.data;
  const pageReady = enabled && Boolean(page.data) && !page.isError;

  useEffect(() => {
    setReceiptError(null);
    reconciled.current.clear();
    reconciledThrough.current = 0;
    ready.current = null;
    continuation.current = null;
    setOlderLoading(false);
    const pendingWrites = writes.current;
    return () => {
      for (const controller of pendingWrites) controller.abort();
      pendingWrites.clear();
    };
  }, [scopeKey]);

  useEffect(() => {
    if (!scope || !page.data || !enabled || !isCurrent(identity)) return;
    const incoming = page.data;
    continuation.current = incoming.cursor;
    setState((previous) =>
      receiveVerifiedChanges(
        previous ?? emptyVerifiedChangeState(scope),
        scope,
        incoming.items,
        previous?.scopeKey === scopeKey && previous.hydrated ? 'poll' : 'hydrate',
        incoming.serverTimestamp,
        performance.now(),
        reducedMotionRef.current,
      ),
    );
  }, [enabled, identity, isCurrent, page.data, scope, scopeKey]);

  useEffect(() => {
    if (!scope || !pageReady || !isCurrent(identity)) return;
    const socket = retainSocket();
    const subscribe = () => {
      for (const [outcome, definition] of Object.entries(VERIFIED_CHANGE_CATALOG)) {
        if (filter.outcomes && !filter.outcomes.some((value) => value === outcome)) continue;
        if (hasPermission(scope.permissions, definition.permission))
          socket.emit('realtime.subscribe', { topic: definition.topic });
      }
    };
    const arm = (serverTimestamp: string) => {
      if (!isCurrent(identity)) return;
      ready.current = {
        scopeKey,
        timestamp: Math.max(
          Date.parse(serverTimestamp),
          Date.parse(latestPage.current?.serverTimestamp ?? serverTimestamp),
        ),
      };
      setState((previous) =>
        readyVerifiedChanges(previous ?? emptyVerifiedChangeState(scope), scope, serverTimestamp),
      );
      subscribe();
    };
    const onReady = (value: unknown) => {
      const parsed = readySchema.safeParse(value);
      if (
        !parsed.success ||
        parsed.data.tenantId !== scope.tenantId ||
        parsed.data.userId !== scope.userId
      )
        return;
      arm(parsed.data.timestamp);
    };
    const onEvent = (value: RealtimeEventItem) => {
      if (!isCurrent(identity) || !isSocketReady() || !value || typeof value !== 'object') return;
      const change = readVerifiedChangeEvent(value);
      if (!change) return;
      if (filter.outcomes && !filter.outcomes.includes(change.outcome)) return;
      if (filter.entityIds && !filter.entityIds.includes(change.entityId)) return;
      if (
        change.tenantId !== scope.tenantId ||
        (change.recipientUserId !== null && change.recipientUserId !== scope.userId) ||
        !hasPermission(scope.permissions, change.permission)
      )
        return;
      const current = stateRef.current,
        measured = Date.parse(change.timestamp);
      if (
        current?.scopeKey === scopeKey &&
        current.hydrated &&
        ready.current?.scopeKey === scopeKey &&
        measured > ready.current.timestamp &&
        measured > Date.parse(current.baselineTimestamp ?? change.timestamp) &&
        measured > Date.parse(current.retiredThroughTimestamp ?? '1970-01-01T00:00:00Z') &&
        measured > reconciledThrough.current &&
        !current.observedIds.includes(change.id) &&
        !reconciled.current.has(change.id)
      ) {
        reconciled.current.add(change.id);
        if (reconciled.current.size > 2048) {
          reconciled.current.clear();
          reconciledThrough.current = measured;
        }
        try {
          void Promise.resolve(onNewChange.current?.(change)).catch(() => {
            if (isCurrent(identity))
              setReceiptError({
                scopeKey,
                error: new Error('Current source refresh is unavailable'),
              });
          });
        } catch {
          if (isCurrent(identity))
            setReceiptError({
              scopeKey,
              error: new Error('Current source refresh is unavailable'),
            });
        }
      }
      setState((previous) =>
        receiveVerifiedChanges(
          previous ?? emptyVerifiedChangeState(scope),
          scope,
          [change],
          'live',
          null,
          performance.now(),
          reducedMotionRef.current,
          isSocketReady(),
        ),
      );
    };
    const quiet = () => {
      if (!isCurrent(identity)) return;
      ready.current = null;
      setState((previous) =>
        previous && previous.scopeKey === scopeKey
          ? {
              ...previous,
              readyTimestamp: null,
              entries: previous.entries.map((entry) => ({ ...entry, pulseStartedAt: null })),
            }
          : previous,
      );
    };
    socket.on('realtime.connected', onReady);
    socket.on('realtime.event', onEvent);
    socket.on('disconnect', quiet);
    socket.on('connect_error', quiet);
    // An already authenticated shared transport may predate this hook. A current,
    // origin-fenced API DB timestamp provides its conservative post-hydration baseline.
    if (isSocketReady() && latestPage.current) arm(latestPage.current.serverTimestamp);
    return () => {
      socket.off('realtime.connected', onReady);
      socket.off('realtime.event', onEvent);
      socket.off('disconnect', quiet);
      socket.off('connect_error', quiet);
      releaseSocket();
    };
  }, [identity, isCurrent, pageReady, scope, scopeKey, filter]);

  useEffect(() => {
    if (!enabled || !scope || state?.scopeKey !== scopeKey || reducedMotion) return;
    const now = performance.now();
    const remaining = state.entries
      .map((entry) =>
        entry.pulseStartedAt === null
          ? 0
          : entry.pulseStartedAt + VERIFIED_CHANGE_ATTENTION_MS - now,
      )
      .filter((value) => value > 0);
    if (!remaining.length) return;
    const timer = window.setTimeout(
      () => refreshClock((value) => value + 1),
      Math.min(...remaining) + 1,
    );
    return () => window.clearTimeout(timer);
  }, [clockRevision, enabled, reducedMotion, scope, scopeKey, state]);

  const markSeen = useCallback(
    async (eventId: string) => {
      if (
        !scope ||
        !enabled ||
        !pageReady ||
        page.isFetching ||
        !isCurrent(identity) ||
        state?.scopeKey !== scopeKey
      ) {
        throw new Error('Current observed-change access is required');
      }
      const entry = state.entries.find((candidate) => candidate.change.id === eventId);
      if (!entry) throw new Error('Review the recorded event before marking it seen');
      const controller = new AbortController();
      writes.current.add(controller);
      setReceiptError(null);
      try {
        const receipt = await verifiedChangeClient.seen(entry.change, scope, {
          signal: controller.signal,
          sessionOrigin: identity.sessionOrigin,
        });
        controller.signal.throwIfAborted();
        if (!isCurrent(identity))
          throw new Error('Access changed while recording this seen receipt');
        setState((previous) =>
          previous ? applyVerifiedChangeSeenReceipt(previous, scope, receipt) : previous,
        );
        return receipt;
      } catch (error) {
        if (isCurrent(identity))
          setReceiptError({
            scopeKey,
            error: error instanceof Error ? error : new Error('Seen receipt unavailable'),
          });
        throw error;
      } finally {
        writes.current.delete(controller);
      }
    },
    [enabled, identity, isCurrent, pageReady, page.isFetching, scope, scopeKey, state],
  );

  const loadOlder = useCallback(async () => {
    if (
      !scope ||
      !enabled ||
      !pageReady ||
      !isCurrent(identity) ||
      !continuation.current ||
      olderLoading
    )
      return;
    const controller = new AbortController();
    writes.current.add(controller);
    setOlderLoading(true);
    try {
      const incoming = await readWindow(
        { sessionOrigin: identity.sessionOrigin, signal: controller.signal },
        continuation.current,
      );
      controller.signal.throwIfAborted();
      if (!isCurrent(identity)) return;
      continuation.current = incoming.cursor;
      setState((previous) =>
        receiveVerifiedChanges(
          previous ?? emptyVerifiedChangeState(scope),
          scope,
          incoming.items,
          'reconnect',
          incoming.serverTimestamp,
          performance.now(),
          reducedMotionRef.current,
        ),
      );
    } catch (error) {
      if (isCurrent(identity))
        setReceiptError({
          scopeKey,
          error: error instanceof Error ? error : new Error('Older observed changes unavailable'),
        });
      throw error;
    } finally {
      writes.current.delete(controller);
      if (isCurrent(identity)) setOlderLoading(false);
    }
  }, [scope, enabled, pageReady, isCurrent, identity, olderLoading, readWindow, scopeKey]);

  const entries =
    pageReady && !page.isFetching && scope && state?.scopeKey === scopeKey
      ? state.entries.map((entry) =>
          verifiedChangePresentation(entry, performance.now(), reducedMotion),
        )
      : [];
  return {
    enabled,
    scopeKey,
    entries,
    isLoading: page.isLoading,
    error: page.error ?? (receiptError?.scopeKey === scopeKey ? receiptError.error : null),
    refetch: page.refetch,
    markSeen,
    loadOlder,
    hasOlder: Boolean(continuation.current),
    isLoadingOlder: olderLoading,
  };
}
